/**
 * EmailVerificationsAdminPage — Admin → Email Verifications.
 *
 * Mounted at /admin/email-verifications. Renders the existing
 * EmailPage in system-mailbox mode (forced to state="VERIFY",
 * with include_system=1 so the verify@ inbox is visible) and
 * pins a floating "Template" button to the bottom-right that
 * opens a slide-over editor for the verification email body.
 *
 * Signature suppression for outbound from this mailbox is enforced
 * server-side in `email_inbox.send_thread_message`.
 */
import React, { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import EmailPage from './EmailPage';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '../components/ui/sheet';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import { Save, RefreshCcw, FileText } from 'lucide-react';

const TemplateSheet = ({ open, onOpenChange, api }) => {
  const [subject, setSubject] = useState('');
  const [htmlBody, setHtmlBody] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showPreview, setShowPreview] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/admin/email-verifications/template');
      setSubject(data.subject || '');
      setHtmlBody(data.html_body || '');
    } catch (e) {
      toast.error('Could not load template');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (open) load(); /* eslint-disable-next-line */ }, [open]);

  const onSave = async () => {
    if (!subject.trim()) { toast.error('Subject required'); return; }
    if (!htmlBody.includes('{{code}}')) {
      toast.error('Body must contain {{code}} placeholder');
      return;
    }
    setSaving(true);
    try {
      await api.put('/admin/email-verifications/template', { subject, html_body: htmlBody });
      toast.success('Template saved');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const previewHtml = htmlBody
    .replaceAll('{{code}}', '482910')
    .replaceAll('{{email}}', 'sample.user@example.com')
    .replaceAll('{{first_name}}', 'Sample');

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="w-full sm:max-w-xl overflow-y-auto"
        data-testid="verify-template-sheet"
      >
        <SheetHeader>
          <SheetTitle>Default verification email</SheetTitle>
          <SheetDescription>
            Sent from <span className="font-mono">verify@collectiveorb.com</span>. The body
            must contain <span className="font-mono">{'{{code}}'}</span>. Optional:{' '}
            <span className="font-mono">{'{{email}}'}</span>,{' '}
            <span className="font-mono">{'{{first_name}}'}</span>.
          </SheetDescription>
        </SheetHeader>

        <div className="space-y-3 mt-4">
          <div className="space-y-1.5">
            <Label className="text-xs">Subject</Label>
            <Input
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              disabled={loading}
              data-testid="verify-template-subject-input"
            />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">HTML body</Label>
            <Textarea
              value={htmlBody}
              onChange={(e) => setHtmlBody(e.target.value)}
              rows={14}
              disabled={loading}
              className="font-mono text-xs"
              data-testid="verify-template-body-input"
            />
          </div>
          <div className="flex flex-wrap gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowPreview((v) => !v)}
              data-testid="verify-template-preview-btn"
            >
              {showPreview ? 'Hide preview' : 'Preview'}
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={load}
              disabled={loading}
              data-testid="verify-template-reload-btn"
            >
              <RefreshCcw className="w-3.5 h-3.5 mr-1" /> Reload
            </Button>
            <div className="flex-1" />
            <Button
              size="sm"
              onClick={onSave}
              disabled={saving || loading}
              data-testid="verify-template-save-btn"
            >
              <Save className="w-3.5 h-3.5 mr-1" /> {saving ? 'Saving…' : 'Save'}
            </Button>
          </div>
          {showPreview && (
            <div className="rounded-lg border border-amber-200 bg-amber-50/40 p-2 mt-2">
              <p className="text-[11px] uppercase tracking-wide text-amber-700 mb-2">
                Preview · placeholder code 482910
              </p>
              <iframe
                title="verify-preview"
                srcDoc={previewHtml}
                className="w-full h-[420px] rounded bg-white border border-amber-200"
                sandbox=""
              />
            </div>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
};

export default function EmailVerificationsAdminPage() {
  const { api } = useAuth();
  const [sheetOpen, setSheetOpen] = useState(false);

  return (
    <>
      <EmailPage forceStateCode="VERIFY" includeSystem={true} />
      <button
        type="button"
        onClick={() => setSheetOpen(true)}
        className="fixed bottom-6 right-6 z-30 rounded-full bg-slate-900 text-white shadow-lg hover:bg-slate-800 px-4 py-3 text-sm font-medium flex items-center gap-2 transition"
        data-testid="verify-template-open-btn"
      >
        <FileText className="w-4 h-4" /> Verification Template
      </button>
      <TemplateSheet open={sheetOpen} onOpenChange={setSheetOpen} api={api} />
    </>
  );
}
